import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Dropdown } from 'ncoded-component-library';
import {
  DropdownProps,
  DropdownRef,
} from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import DotshorizontalIcon from 'icons/Dotshorizontal.icon';
import { DropdownOption } from 'models/Dropdown';
import { ALIGNMENT, Alignment } from 'models/Global';

import './ThreeDotsDropdown.styles.scss';
import './ThreeDotsDropdown.styles.responsive.scss';

type ThreeDotsDropdownProps = {
  className?: string;
  options: DropdownOption[];
  alignment?: Alignment;
  withBorder?: boolean;
} & Partial<Omit<DropdownProps, 'options' | 'ref'>>;

const ThreeDotsDropdown: React.ForwardRefRenderFunction<
  DropdownRef,
  ThreeDotsDropdownProps
> = (props, ref) => {
  const {
    className,
    options,
    trigger,
    withBorder = true,
    alignment = ALIGNMENT.HORIZONTAL,
    ...rest
  } = props;

  const classes = classNames('three-dots-dropdown', className);
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  useImperativeHandle(ref, () => dropdown, [dropdown]);

  const stopEvent = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const dropdownTrigger = useMemo(
    () =>
      trigger || (
        <Button
          className={classNames('three-dots-dropdown__trigger', alignment, {
            withBorder,
          })}
          variant="icon"
          icon={DotshorizontalIcon}
          onMouseDown={stopEvent}
          onMouseUp={stopEvent}
          aria-label="Open"
        />
      ),
    [alignment, trigger, withBorder, stopEvent],
  );

  return (
    <Dropdown
      ref={dropdownRef}
      className={classes}
      trigger={dropdownTrigger}
      {...rest}
    >
      <ul
        className="three-dots-dropdown__menu"
        onContextMenu={stopEvent}
        onMouseDown={stopEvent}
        onMouseUp={stopEvent}
      >
        {options.map(({ label, icon, alert, disabled, onClick }, index) => (
          <li
            key={index}
            className={classNames({ delete: alert, disabled })}
            onClick={(e) => {
              if (disabled) return;

              onClick?.(e);
              dropdown.setIsOpen(false);
            }}
          >
            {icon}
            {label}
          </li>
        ))}
      </ul>
    </Dropdown>
  );
};

export default forwardRef(ThreeDotsDropdown);
